<template>
  <div class="app-container">
    <!-- 顶部工具栏 -->
    <div class="filter-container" style="margin-bottom: 10px;">
      <el-button type="primary" size="small" @click="handleMarkAllRead"
                 :disabled="total === 0">全部已读</el-button>
      <el-button size="small" @click="refreshList">刷新</el-button>
      <span class="margin-left" style="color: #909399; font-size: 13px;">{{ statusText }}</span>
    </div>

    <!-- 消息列表 -->
    <el-table v-loading="listLoading" :data="list" border fit highlight-current-row
              @row-click="handleRowClick" style="width: 100%;">
      <el-table-column type="index" label="#" width="50" />
      <el-table-column prop="msgType" label="类型" width="120">
        <template slot-scope="scope">
          <el-tag :type="msgTypeTag(scope.row.msgType)">
            {{ msgTypeText(scope.row.msgType) }}
          </el-tag>
        </template>
      </el-table-column>
      <el-table-column prop="title" label="标题" min-width="200" show-overflow-tooltip />
      <el-table-column prop="createTime" label="时间" width="180" />
      <el-table-column label="状态" width="100">
        <template slot-scope="scope">
          <el-tag v-if="scope.row.isRead === '0'" type="danger" size="mini">未读</el-tag>
          <el-tag v-else type="info" size="mini">已读</el-tag>
        </template>
      </el-table-column>
      <el-table-column label="操作" width="200">
        <template slot-scope="scope">
          <el-button v-if="scope.row.isRead === '0'" type="text" size="mini"
                     @click.stop="handleMarkRead(scope.row)">标记已读</el-button>
          <el-button type="text" size="mini"
                     @click.stop="openDetail(scope.row)">查看详情</el-button>
        </template>
      </el-table-column>
    </el-table>

    <!-- 分页 -->
    <div class="pagination-container" style="margin-top: 15px; text-align: right;">
      <el-pagination
        v-show="total > 0"
        :current-page="listQuery.page"
        :page-size="listQuery.row"
        :total="total"
        layout="total, prev, pager, next, jumper"
        @current-change="handleCurrentChange" />
    </div>

    <!-- 消息详情弹窗（数据来自列表行，单条详情接口已下线） -->
    <el-dialog :title="detailRow.title || '消息详情'" :visible.sync="detailVisible"
               width="600px" append-to-body>
      <el-row :gutter="10">
        <el-col :span="24" class="detail-row">
          <label class="detail-label">类型：</label>
          <el-tag :type="msgTypeTag(detailRow.msgType)">{{ msgTypeText(detailRow.msgType) }}</el-tag>
        </el-col>
        <el-col :span="24" class="detail-row">
          <label class="detail-label">时间：</label>
          <span>{{ detailRow.createTime }}</span>
        </el-col>
        <el-col :span="24" class="detail-row">
          <label class="detail-label">报修单号：</label>
          <span class="copyable-id" :title="'点击复制 ' + currentRepairId"
                @click="copyRepairId">
            {{ currentRepairId }}
            <i class="el-icon-document-copy"></i>
          </span>
        </el-col>
        <template v-if="contentFields.length > 0">
          <el-col :span="24" class="detail-row" v-for="f in contentFields" :key="f.key">
            <label class="detail-label">{{ f.label }}：</label>
            <span>{{ f.value }}</span>
          </el-col>
        </template>
        <el-col :span="24" class="detail-row" v-if="contentObj.context">
          <label class="detail-label">备注：</label>
          <span>{{ contentObj.context }}</span>
        </el-col>
      </el-row>
      <div slot="footer">
        <el-button @click="detailVisible = false">关 闭</el-button>
        <el-button type="primary" @click="handleGotoBusiness">去处理</el-button>
      </div>
    </el-dialog>
  </div>
</template>

<script>
import { listMessages, markRead, markAllRead } from '@/api/message/messageBoxApi'

export default {
  name: 'MessageBoxList',
  // 复用同一组件承载「全部/未读/已读」三种视图
  props: {
    status: {
      type: String,
      default: 'all' // all | unread | read
    }
  },
  data() {
    return {
      list: [],
      total: 0,
      listLoading: false,
      listQuery: { page: 1, row: 10 },
      // 详情弹窗
      detailVisible: false,
      detailRow: {},
      contentObj: {}
    }
  },
  computed: {
    statusText() {
      return { all: '全部消息', unread: '未读消息', read: '已读消息' }[this.status] || '消息'
    },
    // 报修单号：优先取消息 refId，其次 content 中的 repairId
    currentRepairId() {
      return this.detailRow.refId || this.contentObj.repairId || '-'
    },
    // 把 content JSON 中可展示的业务字段映射成 label-value 列表
    contentFields() {
      const c = this.contentObj
      const fields = [
        { key: 'repairName', label: '报修项目', value: c.repairName },
        { key: 'repairObjName', label: '报修位置', value: c.repairObjName },
        { key: 'tel', label: '联系电话', value: c.tel },
        { key: 'appointmentTime', label: '预约时间', value: c.appointmentTime }
      ]
      return fields.filter(f => f.value)
    }
  },
  watch: {
    // 在「全部/未读/已读」之间切换路由时，组件实例被复用，这里重新拉取
    status() {
      this.refreshList()
    }
  },
  created() {
    this.getList()
  },
  methods: {
    getList() {
      this.listLoading = true
      const params = { page: this.listQuery.page, row: this.listQuery.row }
      if (this.status === 'unread') {
        params.isRead = '0'
      } else if (this.status === 'read') {
        params.isRead = '1'
      }
      listMessages(params).then(res => {
        this.list = res.data || []
        this.total = res.total || 0
      }).catch(err => {
        console.error(err)
      }).finally(() => {
        this.listLoading = false
      })
    },
    refreshList() {
      this.listQuery.page = 1
      this.getList()
    },
    handleCurrentChange(page) {
      this.listQuery.page = page
      this.getList()
    },
    handleMarkRead(row) {
      markRead([row.msgId]).then(() => {
        this.$message.success('已标记已读')
        this.getList()
        // 通知全局轮询刷新一次未读数
        this.$root.$emit('messageBox:refreshUnread')
      })
    },
    handleMarkAllRead() {
      markAllRead().then(() => {
        this.$message.success('已全部标记已读')
        this.getList()
        this.$root.$emit('messageBox:refreshUnread')
      })
    },
    handleRowClick(row) {
      this.openDetail(row)
    },
    openDetail(row) {
      this.detailRow = row
      // content 为 JSON 字符串，需二次解析
      let parsed = {}
      try {
        parsed = JSON.parse(row.content) || {}
      } catch (e) {
        parsed = {}
      }
      this.contentObj = parsed
      this.detailVisible = true
      // 未读消息打开即标记已读
      if (row.isRead === '0') {
        markRead([row.msgId]).then(() => {
          row.isRead = '1'
          this.getList()
          this.$root.$emit('messageBox:refreshUnread')
        })
      }
    },
    // 点击复制报修单号（clipboard API 优先，失败时回退 execCommand）
    copyRepairId() {
      const text = this.currentRepairId
      if (!text || text === '-') {
        return
      }
      const done = () => this.$message({ message: '报修单号已复制：' + text, type: 'success', duration: 2000 })
      const fail = () => this.$message.error('复制失败，请手动选择文本复制')
      if (navigator.clipboard && window.isSecureContext) {
        navigator.clipboard.writeText(text).then(done).catch(() => this._fallbackCopy(text, done, fail))
      } else {
        this._fallbackCopy(text, done, fail)
      }
    },
    _fallbackCopy(text, done, fail) {
      const textarea = document.createElement('textarea')
      textarea.value = text
      textarea.style.position = 'fixed'
      textarea.style.opacity = '0'
      document.body.appendChild(textarea)
      textarea.select()
      try {
        document.execCommand('copy') ? done() : fail()
      } catch (e) {
        fail()
      }
      document.body.removeChild(textarea)
    },
    handleGotoBusiness() {
      const refId = this.detailRow.refId || this.contentObj.repairId
      if (!refId) {
        this.$message.info('该消息暂无关联业务')
        return
      }
      const type = this.detailRow.msgType
      if (type === 'REPAIR_NEW' || type === 'REPAIR_DISPATCH') {
        this.detailVisible = false
        this.$router.push({
          path: '/views/work/repairDetail',
          query: { repairId: refId }
        })
      } else {
        // 兼容后续扩展的未知类型
        this.$message.info('暂不支持该类型的业务跳转')
      }
    },
    msgTypeText(type) {
      const map = { REPAIR_NEW: '新报修', REPAIR_DISPATCH: '派单' }
      return map[type] || '消息'
    },
    msgTypeTag(type) {
      const map = { REPAIR_NEW: 'danger', REPAIR_DISPATCH: 'warning' }
      return map[type] || ''
    }
  }
}
</script>

<style scoped>
.detail-row {
  margin-bottom: 12px;
  line-height: 24px;
}
.detail-label {
  display: inline-block;
  width: 80px;
  color: #909399;
}
.copyable-id {
  font-family: Menlo, Consolas, "Courier New", monospace;
  color: #409EFF;
  cursor: pointer;
  user-select: all;
  word-break: break-all;
}
.copyable-id:hover {
  color: #66b1ff;
  text-decoration: underline;
}
.copyable-id i {
  margin-left: 4px;
  font-size: 13px;
}
</style>
